import AsyncSelect from 'react-select/async';
import CreatableAsyncSelect from 'react-select/async-creatable';
import { Box, FormHelperText, useTheme } from '@mui/material';
import { forwardRef } from 'react';
import { Controller } from 'react-hook-form';
import { observer } from 'mobx-react';
import { useLocalization } from '../../providers/LocalizationProvider';

const AutoComplete = forwardRef(
  (
    {
      name,
      control,
      rules,
      error = false,
      helperText = '',
      defaultOptions,
      loadOptions,
      overwriteProps = {},
      creatable = false,
      isMulti = false,
      inPopUp = false,
      onChangeOther = () => undefined,
      showDropDown = true,
      placeholder,
      disabled = false,
      ...props
    },
    ref,
  ) => {
    const { formatMessage } = useLocalization();
    const theme = useTheme();
    const SelectComponent = creatable ? CreatableAsyncSelect : AsyncSelect;

    const getBorderColor = (fsError, state) => {
      if (fsError?.message) {
        return theme.palette.error.main;
      }
      if (state.isFocused) {
        return theme.palette.primary.main;
      }
      return theme.palette.mode === 'dark'
        ? theme.palette.neutral.strong
        : theme.palette.grey[400];
    };

    return (
      <Box>
        <Controller
          control={control}
          name={name}
          rules={rules}
          render={({ field, fieldState: { error: fsError } }) => {
            return (
              <SelectComponent
                {...field}
                {...props}
                {...(disabled && { isDisabled: true })}
                {...(!showDropDown
                  ? {
                      components: {
                        DropdownIndicator: () => null,
                        IndicatorSeparator: () => null,
                      },
                    }
                  : {})}
                isMulti={isMulti}
                value={field?.value?.label === '' ? null : (field?.value ?? '')}
                error={Boolean(fsError?.message)}
                helperText={fsError?.message || helperText || ''}
                ref={ref}
                defaultOptions={defaultOptions}
                loadOptions={loadOptions}
                placeholder={
                  placeholder
                    ? `${placeholder} (${formatMessage({
                        id: 'TYPE_TO_SEARCH',
                        defaultMessage: 'Type to search',
                      })})`
                    : formatMessage({
                        id: 'TYPE_TO_SEARCH',
                        defaultMessage: 'Type to search',
                      })
                }
                onChange={option => {
                  field.onChange(option);
                  if (onChangeOther) {
                    onChangeOther(option, field?.value);
                  }
                }}
                menuPortalTarget={document.body}
                theme={selectTheme =>
                  theme.palette.mode === 'light'
                    ? selectTheme
                    : {
                        ...selectTheme,
                        colors: {
                          ...selectTheme.colors,
                          neutral0: theme.palette.background.paper,
                          neutral5: theme.palette.neutral.subtle,
                          neutral10: theme.palette.neutral.muted,
                          neutral20: theme.palette.neutral.default,
                          neutral30: theme.palette.neutral.strong,
                          neutral40: theme.palette.text.disabled,
                          neutral50: theme.palette.text.secondary,
                          neutral60: theme.palette.text.secondary,
                          neutral80: theme.palette.text.primary,
                          primary: theme.palette.primary.main,
                          primary25: theme.palette.primaryContainer,
                          primary50: theme.palette.primary.light,
                          primary75: theme.palette.primary.dark,
                        },
                      }
                }
                styles={{
                  control: (baseStyles, state) => {
                    return {
                      ...baseStyles,
                      borderColor: getBorderColor(fsError, state),
                      borderRadius: theme.shape.borderRadius,
                      '&:hover': { borderColor: theme.palette.tertiary.main },
                      marginTop: '-4px',
                    };
                  },
                  // The input is rendered after SingleValue in the same grid cell. It blocks
                  // hover on the value chip when: (1) isHidden (opacity 0 after blur+select),
                  // or (2) focused/menu open with a value but empty filter — react-select then
                  // sets isHidden false while SingleValue is still under the input.
                  input: (baseStyles, state) => {
                    const inputValue = state.selectProps?.inputValue ?? '';
                    const passPointerEventsToValue =
                      state.isHidden ||
                      (!state.isMulti && state.hasValue && inputValue === '');
                    return {
                      ...baseStyles,
                      ...(passPointerEventsToValue
                        ? { pointerEvents: 'none' }
                        : {}),
                    };
                  },

                  ...(inPopUp && {
                    menuPortal: base => ({ ...base, zIndex: 9999 }),
                  }),
                }}
                {...overwriteProps}
              />
            );
          }}
        />
        {error || helperText ? (
          <FormHelperText sx={{ typography: 'body2' }} error={Boolean(error)}>
            {error || helperText}
          </FormHelperText>
        ) : null}
      </Box>
    );
  },
);

export default observer(AutoComplete);
